import type { FormEvent, ReactNode } from "react"
import { Button, Card } from "@alascorporation/ui"
import Alert from "../ui/alert/Alert"
import { EntityDetailNavLayout, type EntityDetailNavGroup } from "./EntityDetailNav"
import {
  type StudentProfileFields,
  type SectionId,
  StudentProfileSections,
  SECTION_TITLES,
  completionPercent,
  completionTone,
  sectionStatus,
  COMPLETION_TONE_CLASS,
} from "./StudentProfileSections"

const NAV_GROUPS: { label: string; sections: SectionId[] }[] = [
  { label: "PERSONAL", sections: ["personal", "document", "location"] },
  { label: "SALUD Y FAMILIA", sections: ["health", "guardian"] },
  { label: "ACADÉMICO", sections: ["institution"] },
]

/**
 * Shared chrome (breadcrumb, hero, section nav, sections, sticky action bar) for both
 * creating a student and editing an existing one, so the two flows stay visually and
 * structurally identical — only the surrounding page logic (fetch vs. create) differs.
 */
export function StudentProfileEditor({
  breadcrumbCurrent,
  heroTitle,
  heroSubtitleItems,
  profile,
  set,
  documentTypes,
  activeSection,
  onActiveSectionChange,
  personalExtra,
  infoMessage,
  error,
  successMessage,
  onSubmit,
  submitLabel,
  submitDisabled,
  onCancel,
  cancelLabel = "Volver",
  onBackToList,
}: {
  breadcrumbCurrent: string
  heroTitle: string
  heroSubtitleItems: (string | null | undefined)[]
  profile: StudentProfileFields
  set: <K extends keyof StudentProfileFields>(key: K, value: StudentProfileFields[K]) => void
  documentTypes: { value: string; label: string }[]
  activeSection: SectionId
  onActiveSectionChange: (id: SectionId) => void
  personalExtra?: ReactNode
  infoMessage?: string
  error?: string
  successMessage?: string
  onSubmit: (e: FormEvent) => void
  submitLabel: string
  submitDisabled?: boolean
  onCancel: () => void
  cancelLabel?: string
  onBackToList: () => void
}) {
  const percent = completionPercent(profile)
  const tone = completionTone(percent)
  const healthIncomplete = sectionStatus(profile, "health").status !== "complete"
  const guardianIncomplete = sectionStatus(profile, "guardian").status !== "complete"
  const missingSafetyLabels = [
    healthIncomplete ? "salud" : null,
    guardianIncomplete ? "del acudiente" : null,
  ].filter((label): label is string => label !== null)
  const initials = heroTitle
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("")
  const subtitleItems = heroSubtitleItems.filter((item): item is string => Boolean(item))

  const navGroups: EntityDetailNavGroup<SectionId>[] = NAV_GROUPS.map((group) => ({
    label: group.label,
    items: group.sections.map((id) => ({
      key: id,
      label: SECTION_TITLES[id],
      status: sectionStatus(profile, id).status,
    })),
  }))

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <button type="button" onClick={onBackToList} className="hover:text-brand-500">
          Estudiantes
        </button>
        <span>/</span>
        <span className="text-gray-900 dark:text-white font-medium">{breadcrumbCurrent}</span>
      </div>

      <Card className="overflow-hidden rounded-[28px] p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-full bg-primary text-lg font-semibold text-primary-foreground">
              {initials || "?"}
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-foreground leading-tight">{heroTitle}</h1>
              {subtitleItems.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-4">
                  {subtitleItems.map((item) => (
                    <span key={item} className="text-sm text-muted-foreground">
                      {item}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2.5 rounded-full bg-muted px-4 py-2">
            <span className={`text-xl font-bold ${COMPLETION_TONE_CLASS[tone]}`}>{percent}%</span>
            <span className="text-xs text-muted-foreground">del perfil completado</span>
          </div>
        </div>
        <div className="mt-4 h-2 rounded-full bg-muted overflow-hidden">
          <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${percent}%` }} />
        </div>
        {missingSafetyLabels.length > 0 && (
          <p className="mt-2 text-xs font-medium text-warning-600 dark:text-orange-400">
            Faltan datos de {missingSafetyLabels.join(" y ")}: son clave para actuar rápido si hay una emergencia con este estudiante.
          </p>
        )}
      </Card>

      {infoMessage && <Alert variant="info" title="Este formulario se puede completar por partes" message={infoMessage} />}
      {error && <Alert variant="error" title="No se pudo guardar" message={error} />}
      {successMessage && <Alert variant="success" title="Guardado" message={successMessage} />}

      <form onSubmit={onSubmit}>
        <EntityDetailNavLayout groups={navGroups} activeKey={activeSection} onChange={onActiveSectionChange}>
          <div className="space-y-3.5">
            <StudentProfileSections
              profile={profile}
              set={set}
              documentTypes={documentTypes}
              activeSection={activeSection}
              personalExtra={personalExtra}
            />

            <Card className="sticky bottom-4 z-10 flex flex-wrap items-center justify-between gap-3 rounded-[18px] px-5 py-3.5 shadow-lg">
              <span className="text-[13px] text-muted-foreground">{percent}% completado</span>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={onCancel}>
                  {cancelLabel}
                </Button>
                <Button type="submit" disabled={submitDisabled}>
                  {submitLabel}
                </Button>
              </div>
            </Card>
          </div>
        </EntityDetailNavLayout>
      </form>
    </div>
  )
}
